<template>
  <div class="layout">
    <el-container style="height: 100%;overflow: hidden;">
      <el-aside class="aside" :width="isCollapse ? 'auto' : '240px'">
        <template v-if="env === 'sxsw'">
          <div class="logo" v-if="isCollapse"></div>
          <div class="logo" v-else>
            <span>&nbsp;&nbsp;</span>
          </div>
        </template>
        <template v-else>
          <div class="brand-logo" :class="{ collapsed: isCollapse }">
            <img
              v-if="isCollapse"
              :src="brandFavicon"
              :alt="brandName"
              class="brand-icon"
            />
            <img
              v-else
              :src="brandBanner"
              :alt="brandName"
              class="brand-banner"
            />
          </div>
        </template>
        <el-menu
          :default-active="activeMenu"
          :collapse="isCollapse"
          @select="handleMenuSelect">
          <template v-for="item in menu" :key="item.id">
            <el-sub-menu v-if="item.type === '1' && item.showFlag" :index="item.id">
              <template #title>
                <el-icon v-if="item.icon">
                  <component :is="item.icon"></component>
                </el-icon>
                <span>{{ item.name }}</span>
              </template>
              <template v-for="value in item.children">
                <el-menu-item
                  v-if="value.showFlag"
                  :key="value.id"
                  :index="value.href">
                  <el-icon v-if="value.icon">
                    <component :is="value.icon"></component>
                  </el-icon>
                  <template #title>{{value.name}}</template>
                </el-menu-item> 
              </template>
            </el-sub-menu>

            <el-menu-item v-if="item.type === '2' && item.showFlag" :index="item.href">
              <el-icon v-if="item.icon">
                <component :is="item.icon"></component>
              </el-icon>
              <template #title>{{item.name}}</template>
            </el-menu-item>
          </template>
        </el-menu>
      </el-aside>
      <el-container>
        <el-header style="background-color: var(--imles-chrome-bg, #fff);height: 48px;padding: 0px;">
          <Header v-model="isCollapse"></Header>
        </el-header>
        <el-main>
          <router-view></router-view>
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue"
import { useRouter, useRoute } from "vue-router"
import { useMenuStore } from "@/store/menu"
import { useCommonStore } from "@/store/common"
import { useProjectInfoStore } from "@/store/project"
import { getMenuList } from "@/api/common"

const menuStore = useMenuStore()
const commonStore = useCommonStore()
const projectInfoStore = useProjectInfoStore()


const env = import.meta.env.VITE_APP_ENV;
const brand = (import.meta.env.VITE_BRAND || 'imles') as 'imles' | 'mycoach';
const brandName = brand === 'mycoach' ? 'Mycoach Health' : '医合易生';
const defaultFavicon = brand === 'mycoach'
  ? new URL('../../assets/img/login/mycoach_favicon.png', import.meta.url).href
  : new URL('../../assets/img/login/imles_favicon.ico', import.meta.url).href;
const defaultBanner = brand === 'mycoach'
  ? new URL('../../assets/img/login/mycoach_logo.png', import.meta.url).href
  : new URL('../../assets/img/login/imles_logo.png', import.meta.url).href;
// Once a project is opened, its uploaded logo pair replaces the default
// brand logos in the workbench sidebar — falls back to the brand logos
// when the project has no custom logos configured.
const brandFavicon = computed(
  () => projectInfoStore.projectInfo?.squareLogoUrl || defaultFavicon,
);
const brandBanner = computed(
  () => projectInfoStore.projectInfo?.companyLogoUrl || defaultBanner,
);
const router = useRouter();
const route = useRoute();
const activeMenu = ref("/home")
const isCollapse = computed({
  get () {
    return commonStore.isCollapse;
  },
  set () {
    commonStore.changeCollapse()
  }
});

const menu = computed(() => {
  return menuStore.personMenu;
})


const handleMenuSelect = (key: string) => {
  router.push({
    path: key
  })
}

watch(route, (route:any) => {
  activeMenu.value = route.path;
}, { immediate: true })

onBeforeMount(() => {
  getMenuList({ groupCode: 'index' }).then((result) => {
    const { menus, permissions } = result.data;
    menuStore.setPersonMenu(menus, permissions);
    menuStore.getPersonMenu();
  }).catch(() => {});
})
</script>

<style lang="scss" scoped>
.layout {
  height: 100%;
  background-color: #F5F7F9;
  overflow: hidden;
  .aside {
    height: 100%;
    background-color: #fff;
    border-right: 1px solid #DBDBDB;
    overflow-x: hidden;
  }
  .logo {
    display: flex;
    align-items: center;
    font-family: var(--imles-font-family-base);
    font-weight: 600;
    font-size: 22px;
    color: rgba(0,0,0,0.9);
    padding-left: 21px;
    padding-top: 17px;
    user-select: none;
    margin-bottom: 18px;
    &::before {
      display: block;
      content: "";
      width: 22px;
      height: 29px;
      background-image: url("../assets/logo.png");
      background-repeat: no-repeat;
      background-size: cover;
      margin-right: 17px;
    }
  }
  .brand-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 64px;
    user-select: none;
    margin-bottom: 18px;
    overflow: hidden;
    .brand-icon {
      width: 32px;
      height: 32px;
      object-fit: contain;
    }
    .brand-banner {
      max-width: 200px;
      max-height: 56px;
      width: auto;
      height: auto;
      object-fit: contain;
    }
    &.collapsed {
      padding-left: 0;
      justify-content: center;
    }
  }
  .menu {
    list-style: none;
    margin: 0px;
    padding: 0px;
    font-family: var(--imles-font-family-base);
    font-weight: 500;
    font-size: 14px;
    color: #333333;
    line-height: 22px;
    text-align: left;
    font-style: normal;
    padding-top: 40px;
    li {
      user-select: none;
      cursor: pointer;
      position: relative;
      height: 40px;
      padding-left: 20px;
      display: flex;
      align-items: center;
      transition: all 0.2s ease;
      &::before {
        display: block;
        content: "";
        width: 13px;
        height: 13px;
        background-repeat: no-repeat;
        background-size: cover;
        margin-right: 13px;
      }
      &::after {
        display: block;
        content: "";
        width: 3px;
        height: 40px;
        position: absolute;
        right: 0px;
        top: 0px;
        background-color: #fff;
        transition: all 0.2s ease;
      }
    }
    li:hover {
      background-color: var(--imles-menu-hover-bg);
      color: var(--imles-primary);
      &::after {
        background-color: var(--imles-primary);
      }
    }
    li.active {
      background-color: var(--imles-menu-hover-bg);
      color: var(--imles-primary);
      &::after {
        background-color: var(--imles-primary);
      }
    }
    li:nth-child(1)::before {
      background-image: url("../assets/note.png");
    }
    li:nth-child(2)::before {
      background-image: url("../assets/template.png");
    }
  }
  .main-box {
    display: flex;
    gap: 20px;
    .main-left {
      width: 286px;
      flex-shrink: 1;
      .title {
        font-family: var(--imles-font-family-base);
        font-weight: 600;
        font-size: 20px;
        color: rgba(0,0,0,0.9);
        line-height: 28px;
        font-style: normal;
        margin: 0px;
        margin-bottom: 24px;
      }
      .avatar {
        width: 104px;
        height: 104px;
        border-radius: 50%;
        margin: 60px auto 32px;
        background-color: #eee;
        background-repeat: repeat;
        background-size: cover;
        background-image: url('../assets/head.png');
      }
      .user-info-item {
        font-family: var(--imles-font-family-base);
        font-weight: 500;
        font-size: 14px;
        color: #333333;
        line-height: 20px;
        text-align: center;
        font-style: normal;
        margin-bottom: 16px;
      }
      .tree-box {
        display: block;
      }
      .left-item {
        position: relative;
        padding: 28px 20px;
        height: 486px;
        background-color: #fff;
        border-radius: 8px;
        margin-bottom: 20px;
      }
    }
    .main-right {
      flex: 1;
      .title {
        font-family: var(--imles-font-family-base);
        font-weight: 600;
        font-size: 18px;
        color: rgba(0,0,0,0.9);
        line-height: 25px;
        text-align: left;
        font-style: normal;
        margin: 0px;
        padding-bottom: 17px;
        border-bottom: 1px solid #E1E1E1;
      }
      .btn-box {
        position: absolute;
        right: 28px;
        top: 18px;
      }
      .input-box {
        height: 380px;
        overflow-x: hidden;
        overflow-y: auto;
        padding-bottom: 20px;
      }
      .gen-box {
        box-sizing: border-box;
        width: 100%;
        height: 70px;
        line-height: 70px;
        background: #FFFFFF;
        box-shadow: 0px -6px 32px 5px rgba(0,0,0,0.04);
        border-radius: 0px 0px 8px 8px;
        position: absolute;
        bottom: 0px;
        left: 0px;
        text-align: center;
        padding: 0px 28px;
      }
      .right-item {
        position: relative;
        padding: 28px 26px;
        height: 486px;
        background-color: #fff;
        border-radius: 8px;
        margin-bottom: 20px;
      }
    }
  }
}
:deep(.create-btn) {
  position: absolute;
  top: 28px;
  right: 20px;
}
:deep(.el-textarea) {
  --el-input-focus-border: var(--imles-primary);
  --el-input-focus-border-color: var(--imles-primary);
}
:deep(.el-menu) {
  --el-menu-border-color: #fff;
}
</style>